// src/pages/Contact.jsx
import { motion } from 'framer-motion';
import { MainLayout } from 'components/Layout';
import { useEffect } from 'react';

export const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MainLayout
      title="Contact — Northup Technologies"
      description="Reach out to Northup for partnerships, support, and business inquiries."
    >
      <div className="mx-auto max-w-6xl pb-24">
        {/* HERO */}
        <motion.section
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
          className="mb-16 space-y-6 text-center"
        >
          <span className="inline-block rounded-full border border-slate-700/70 bg-slate-900/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.21em] text-cyan-400 backdrop-blur">
            Contact Us
          </span>

          <h1 className="text-4xl font-semibold text-slate-50 sm:text-5xl lg:text-6xl">
            Let’s Connect
          </h1>

          <p className="mx-auto max-w-2xl text-sm text-slate-400 sm:text-base">
            Whether you’re a business, investor, creator, or user — our team is ready to talk.
          </p>
        </motion.section>

        {/* MAIN GRID */}
        <div className="grid gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          {/* LEFT — CONTACT FORM */}
          <motion.section
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 shadow-xl backdrop-blur-xl"
          >
            <h2 className="mb-6 text-2xl font-semibold text-slate-50">Send us a message</h2>

            <form className="space-y-6">
              <FormField label="Full Name" name="name" placeholder="John Doe" />

              <FormField label="Email" name="email" type="email" placeholder="you@example.com" />

              <FormField label="Subject" name="subject" placeholder="e.g. Partnership request" />

              <div>
                <label className="text-sm font-medium text-slate-300">Message</label>
                <textarea
                  rows="6"
                  className="mt-2 w-full rounded-xl border border-slate-700/70 bg-slate-900/60 px-4 py-3 text-slate-200 shadow-inner placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                  placeholder="Write your message..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full rounded-full bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 px-6 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:brightness-110"
              >
                Send Message
              </button>
            </form>
          </motion.section>

          {/* RIGHT — CONTACT INFO */}
          <motion.section
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="space-y-10"
          >
            {/* Info block */}
            <div className="rounded-2xl border border-slate-800/60 bg-slate-900/50 p-8 backdrop-blur-xl shadow-xl">
              <h3 className="text-xl font-semibold text-slate-50">Contact Information</h3>

              <div className="mt-6 space-y-4 text-sm text-slate-300">
                <p>
                  <span className="font-semibold text-slate-100">Phone:</span> +256 (0) 770 000 000
                </p>

                <p>
                  <span className="font-semibold text-slate-100">Email:</span> support@northup.ug
                </p>

                <p>
                  <span className="font-semibold text-slate-100">General:</span> hello@northup.ug
                </p>

                <p>
                  <span className="font-semibold text-slate-100">Support Hours:</span> Mon–Fri,
                  9am–6pm (EAT)
                </p>
              </div>

              <div className="mt-6 flex gap-3 text-sm">
                <a className="rounded-full border border-slate-700/70 bg-slate-900/70 px-4 py-2 text-slate-300 hover:border-slate-500 hover:text-slate-50">
                  Twitter
                </a>
                <a className="rounded-full border border-slate-700/70 bg-slate-900/70 px-4 py-2 text-slate-300 hover:border-slate-500 hover:text-slate-50">
                  LinkedIn
                </a>
                <a className="rounded-full border border-slate-700/70 bg-slate-900/70 px-4 py-2 text-slate-300 hover:border-slate-500 hover:text-slate-50">
                  Instagram
                </a>
                <a className="rounded-full border border-slate-700/70 bg-slate-900/70 px-4 py-2 text-slate-300 hover:border-slate-500 hover:text-slate-50">
                  Justknow
                </a>
              </div>
            </div>

            {/* Address / Map block */}
            <motion.div
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative h-64 overflow-hidden rounded-2xl border border-slate-800/60 bg-slate-900/50 shadow-lg"
            >
              {/* Map placeholder */}
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-fuchsia-500/10 mix-blend-overlay"
              />

              <div className="absolute inset-0 flex flex-col items-center justify-center space-y-2">
                <div className="relative flex h-20 w-20 items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-cyan-500/20 blur-xl" />
                  <div className="absolute h-14 w-14 rounded-full border-2 border-cyan-400/60 bg-slate-900/70 backdrop-blur" />
                  <div className="absolute h-3 w-3 rounded-full bg-cyan-400" />
                </div>

                <p className="text-xs text-slate-400">Kampala, Uganda</p>
              </div>

              {/* Address overlaid */}
              <div className="absolute bottom-4 left-4 rounded-lg bg-slate-900/70 px-4 py-2 text-sm text-slate-300 backdrop-blur-lg">
                Uganda Central Kampala,
                <br />
                Central Division Nakasero II Lourdel,
                <br />
                P.O.Box 150570 Kampala GPO 7 Lourdel
              </div>
            </motion.div>
          </motion.section>
        </div>

        {/* FINAL CTA */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto mt-20 max-w-2xl rounded-3xl border border-slate-800 bg-slate-900/60 p-10 text-center shadow-[0_0_40px_rgba(56,189,248,0.12)] backdrop-blur-xl"
        >
          <h3 className="text-2xl font-semibold text-slate-50">We’re here to help you grow.</h3>
          <p className="mt-3 text-sm text-slate-400">
            Whether you want to collaborate, integrate, or join our mission — reach out anytime.
          </p>

          <button className="mt-6 rounded-full bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 px-6 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:brightness-110">
            Message the Team
          </button>
        </motion.div>
      </div>
    </MainLayout>
  );
};

/* ----------------------------------------------- *
 * Reusable Form Field Component
 * ----------------------------------------------- */

const FormField = ({ label, name, type = 'text', placeholder }) => (
  <div>
    <label className="text-sm font-medium text-slate-300">{label}</label>
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      className="mt-2 w-full rounded-xl border border-slate-700/70 bg-slate-900/60 px-4 py-3 text-slate-200 shadow-inner placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
    />
  </div>
);
